import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { DualLangInputComponent } from '../../../../../../../../../form-builder/lib/dual-lang-input/dual-lang-input.component';
import { JobTitleService } from '../../../../../job-title/job-title.service';
import { PaginatedSelectComponent } from '../../../../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { InputComponent } from '../../../../../../../../../ui-components/lib/input/input.component';
import { ValidationMessageComponent } from '../../../../../../../../../ui-components/lib/validation-message/validation-message.component';
import {
  LookupFilter,
  LookupOptionItem,
} from '../../../../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { FormService } from '../../../../../../../../../form-builder/services/form/form.service';
import { dualLanguageCustomValidators } from '../../../../../../../../../form-builder/services/form/custom-validators';
import { FormValidation } from '../../../../../../../../../ui-components/enums/form-validation';
import { ToasterService } from '../../../../../../../../../ui-components/services/toast/toast.service';
import { filterInputTypesEnum } from '../../../../../../../../../ui-components/enums/filter-input-type';
import { ENDPOINTS } from '../../../../../../../../../core/common/constants/endpoints.constants';
import { QuickCreatedItem } from './quick-create-model';

@Component({
  selector: 'lib-job-title-quick-create-form',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    DualLangInputComponent,
    PaginatedSelectComponent,
    InputComponent,
    ValidationMessageComponent,
  ],
  templateUrl: './job-title-quick-create-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobTitleQuickCreateFormComponent implements OnInit {
  private readonly jobTitleService = inject(JobTitleService);
  private readonly formService = inject(FormService);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  readonly prefillName = input<string>('');

  readonly created = output<LookupOptionItem>();
  readonly cancelled = output<void>();

  readonly isLoading = signal(false);

  protected readonly variableOptionsApi = ENDPOINTS.Lookup.VariableOptionsSorted;

  private readonly baseVariableFilters = (key: string): LookupFilter[] => [
    {
      key: 'variable.module',
      value: 'user_management',
      value_type: filterInputTypesEnum.STATIC,
    },
    { key: 'variable.key', value: key, value_type: filterInputTypesEnum.STATIC },
    { key: 'is_active', value: '1', value_type: filterInputTypesEnum.STATIC },
  ];

  protected readonly trackFilter = this.baseVariableFilters('career_track');
  protected readonly radfordGradeFilter =
    this.baseVariableFilters('radford_grade');
  protected readonly jobFamilyFilter = this.baseVariableFilters('job_family');

  readonly formGroup = new FormGroup({
    name: new FormGroup(
      {
        en: new FormControl('', [
          Validators.minLength(2),
          Validators.maxLength(FormValidation.maxLength),
        ]),
        ar: new FormControl('', [
          Validators.minLength(2),
          Validators.maxLength(FormValidation.maxLength),
        ]),
      },
      { validators: [dualLanguageCustomValidators.atLeastOneLangRequired] }
    ),
    identifier: new FormControl('', [
      Validators.minLength(5),
      Validators.maxLength(10),
    ]),
    track_option_id: new FormControl<number | null>(null),
    radford_grade_option_id: new FormControl<number | null>(null),
    job_family_option_id: new FormControl<number | null>(null),
  });

  ngOnInit(): void {
    if (this.prefillName()) {
      this.formGroup.get('name.en')?.setValue(this.prefillName());
    }
  }

  submit(): void {
    this.formService.syncDualLangControls(this.formGroup);

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    const value = this.formGroup.getRawValue();

    this.jobTitleService
      .addJobTitle({
        name: { en: value.name?.en, ar: value.name?.ar },
        identifier: value.identifier ?? '',
        track_option_id: value.track_option_id,
        radford_grade_option_id: value.radford_grade_option_id,
        job_family_option_id: value.job_family_option_id,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: object) => {
          this.isLoading.set(false);
          const data = (response as { data: QuickCreatedItem }).data;
          this.created.emit({
            id: data.id,
            name: data.name,
            value: data.id,
            label: data.name,
          });
        },
        error: error => this.handleError(error),
      });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  private handleError(error: {
    errors?: { [key: string]: string[] };
    message?: string;
    error?: { message?: string };
  }): void {
    this.isLoading.set(false);
    this.formService.addParentFormGroupErrors(
      error as { errors: { [key: string]: string[] }; message: string },
      this.formGroup
    );
    this.toasterService.add(
      error.message ? error.message : (error.error?.message ?? ''),
      '',
      '',
      'error',
      true,
      true
    );
  }
}
